/** Date formatting shared by the trip page and list views. */

const fmt = (opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' });

/** Parses YYYY-MM-DD at UTC noon so no timezone can shift the calendar day. */
function parse(date: string): Date | null {
  const d = new Date(`${date}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateRange(start: string | null, end: string | null): string | null {
  if (!start) return null;
  const s = parse(start);
  if (!s) return null;

  if (!end) return fmt({ month: 'long', year: 'numeric' }).format(s);
  const e = parse(end);
  if (!e) return fmt({ month: 'long', year: 'numeric' }).format(s);

  const sameYear = s.getUTCFullYear() === e.getUTCFullYear();
  const sameMonth = sameYear && s.getUTCMonth() === e.getUTCMonth();

  if (sameMonth) {
    return `${fmt({ month: 'long' }).format(s)} ${s.getUTCDate()}–${e.getUTCDate()}, ${e.getUTCFullYear()}`;
  }
  if (sameYear) {
    return `${fmt({ month: 'short', day: 'numeric' }).format(s)} – ${fmt({ month: 'short', day: 'numeric' }).format(e)}, ${e.getUTCFullYear()}`;
  }
  return `${fmt({ month: 'short', year: 'numeric' }).format(s)} – ${fmt({ month: 'short', year: 'numeric' }).format(e)}`;
}

export function hostnameOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

export const faviconFor = (url: string | null): string | null => {
  const host = hostnameOf(url);
  return host ? `https://www.google.com/s2/favicons?domain=${host}&sz=64` : null;
};

export function relativeTime(unixSeconds: number): string {
  const diff = Date.now() / 1000 - unixSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return fmt({ month: 'short', day: 'numeric' }).format(new Date(unixSeconds * 1000));
}
